import { useEffect } from 'react';

import orderBy from 'lodash/orderBy';
import { useField, useForm } from 'react-final-form';

import { countryCodeMap, IS_PROD } from '@cast/constants';
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  Sizes,
} from '@cast/design-system';

import { useCountryCodeQuery } from 'hooks/queries';

import { searchFilter } from './_utils';
import { CountryIcon } from '../_components';

import { RffAutocomplete } from '@cast/shared-rff-ui';

type CountryCodeAutocompleteProps = {
  name: string;
  size?: Sizes<'sm' | 'md' | 'lg'>;
};

const autocompleteOptions = orderBy(
  countryCodeMap,
  ['phone', (o) => o.orderWeight || 0],
  ['asc', 'desc']
);

export const CountryCodeAutocomplete = ({
  name,
  size = 'md',
}: CountryCodeAutocompleteProps) => {
  const field = useField(name);
  const { change } = useForm();

  const { country } = useCountryCodeQuery(IS_PROD);
  useEffect(() => {
    if (!country || field.meta.dirty) {
      return;
    }
    const loadedCountry = countryCodeMap.find((c) => c.code === country);
    if (loadedCountry) {
      change(name, loadedCountry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  return (
    <RffAutocomplete
      name={name}
      size={size}
      sx={{
        '& .MuiInputBase-root ': {
          cursor: 'pointer',
        },
      }}
      options={autocompleteOptions}
      optionLabel="phone"
      searchFilter={searchFilter}
      optionKey={(option) => option.code}
      renderOption={(option, reactProps) => {
        return (
          <ListItem
            {...reactProps}
            startAdornment={
              <ListItemIcon>
                <CountryIcon countryCode={option.code} />
              </ListItemIcon>
            }
          >
            <ListItemText>{option.phone}</ListItemText>
          </ListItem>
        );
      }}
      optionIcon={(item) => {
        return (
          <CountryIcon
            key={item.label}
            countryCode={item.code}
            sx={{
              borderRadius: '2px',
            }}
          />
        );
      }}
      inputProps={{
        placeholder: '+00',
      }}
      virtualization
      virtualizationProps={{
        maxHeight: 142,
      }}
    />
  );
};
